import { Directive, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CurrentLanguageService } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { envConfiguredHostname } from '@shared/utils';

/**
 * A directive to handle HTML link interactions within an Angular application.
 *
 * The `HandleHtmlLinksDirective` intercepts `<a>` (anchor) element click events and selectively routes
 * internal links via Angular's `Router.navigateByUrl()` method. External links, "mailto:" and "tel:"
 * links, as well as invalid or unprocessable ones, are handled by the browser as usual.
 *
 * This directive ensures smooth navigation within a single-page application by avoiding full page
 * reloads for internal links while deferring specific link types (e.g., "mailto:" and external URLs)
 * to their default browser behaviors.
 *
 * @example
 * In the template:
 * ```html
 * <a href="/dashboard" handleHtmlLinks>Dashboard</a>
 * <a href="https://example.com" handleHtmlLinks>External Link</a>
 * ```
 * When the internal link (`/dashboard`) is clicked, Angular's `Router` will navigate to the route without reloading the page.
 * External links (e.g., `https://example.com`) will be opened in their default way (browser/tab behavior).
 *
 * @usageNotes
 * - Use this directive on anchor (`<a>`) elements within your Angular application to enhance internal
 *   navigation experience using the Angular Router.
 * - This directive is designed to support standalone usage by leveraging Angular's `standalone` directive capabilities.
 *
 * @directive
 * @selector [handleHtmlLinks]
 */
@Directive({
  selector: '[handleHtmlLinks]',
  standalone: true,
  host: {
    '(click)': 'onClick($event)',
  },
})
export class HandleHtmlLinksDirective {
  private readonly router = inject(Router);
  private readonly logs = inject(LogsService);
  private readonly language = inject(CurrentLanguageService).language;

  onClick(event: Event): void {
    const anchor = (event.target as HTMLElement).closest('a');
    const href = anchor?.getAttribute('href');

    if (!href || this.shouldLetBrowserHandle(href)) {
      return;
    }

    event.preventDefault();
    const internalPath = this.getInternalPath(href);
    void this.router.navigateByUrl(internalPath);
  }

  /**
   * Determines if the browser should handle the given hyperlink reference (href).
   *
   * This method checks if the URL corresponds to a "mailto:" or "tel:" link, or if
   * it is an external URL. These types of links are typically handled by the
   * browser or a system application, rather than the web application itself.
   *
   * @param href - The hyperlink reference (URL) to be evaluated. It can be a relative, absolute, or special URL string (e.g., "mailto:" or "tel:").
   * @returns A boolean value indicating whether the browser should handle the link. Returns `true` if the href is a "mailto:", "tel:" link, or an external URL; otherwise, returns `false`.
   */
  private shouldLetBrowserHandle(href: string): boolean {
    const isSpecialLink = href.startsWith('mailto:') || href.startsWith('tel:');
    return isSpecialLink || this.isExternalUrl(href);
  }

  /**
   * Determines if the provided URL is an external link.
   *
   * This method checks whether the given URL points to a location outside
   * the configured hostname. It also validates the URL format and handles
   * potential parsing errors gracefully.
   *
   * @param href - The URL string to be evaluated.
   * @returns `true` if the URL is external (i.e., it does not match the configured hostname);
   *          otherwise returns `false`. If the URL is invalid, it is considered external.
   */
  private isExternalUrl(href: string): boolean {
    if (!href.startsWith('http')) {
      return false;
    }

    try {
      const url = new URL(href);
      return url.origin !== envConfiguredHostname(this.language.region);
    } catch (e) {
      this.logs.error('HandleHtmlLinksDirective', `Invalid URL encountered: ${href}`, e);
      return true;
    }
  }

  /**
   * Extracts the internal path from a given URL or returns the input string if it's not a valid URL.
   *
   * This method processes the provided URL to derive the internal path including the pathname, query string,
   * and fragment identifier. If the input is not a fully qualified URL (e.g., does not start with "http" or
   * cannot be parsed as a URL), the method returns the input string unchanged.
   *
   * @param href - The string representing a potential URL or a relative path. If it starts with "http",
   *               the method attempts to parse the string as a URL; otherwise, the string is directly returned.
   *
   * @return A string containing the internal path of the URL including pathname, query, and hash if provided, or
   *         the input string itself if it is not a valid URL or does not start with "http".
   */
  private getInternalPath(href: string): string {
    if (!href.startsWith('http')) {
      return href;
    }

    try {
      const url = new URL(href);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return href;
    }
  }
}
